import { open } from "@tauri-apps/plugin-shell";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { useSetImmerAtom, withImmer } from "jotai-immer";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { uid } from "uid";
import { toast } from "react-toastify";
import { saveFile } from "$/utils/fileSystem.ts";
import { useFileOpener } from "$/hooks/useFileOpener.ts";
import exportTTMLText from "$/modules/project/logic/ttml-writer";
import {
	segmentWord,
	segmentLyricLines,
} from "$/modules/segmentation/utils/segmentation";
import { matchesSavedSyllabificationEngine } from "$/modules/segmentation/utils/detect-syllabification-engine";
import { SYLLABIFICATION_ENGINES } from "$/modules/segmentation/utils/syllabification-engines";
import {
	segmentationEngineAtom,
	segmentationSplitEnglishAtom,
} from "$/modules/segmentation/states";
import { audioEngine } from "$/modules/audio/audio-engine";
import { getSynchronizableUnits } from "$/modules/lyric-editor/utils/lyric-states.ts";
import { validateSections } from "$/modules/lyric-editor/utils/section-system.ts";
import { currentTimeAtom } from "$/modules/audio/states/index.ts";
import { useSegmentationConfig } from "$/modules/segmentation/utils/useSegmentationConfig";
import {
	advancedSegmentationDialogAtom,
	autoSegmentDialogAtom,
	learnedSplitsDialogAtom,
	confirmDialogAtom,
	historyRestoreDialogAtom,
	latencyTestDialogAtom,
	metadataEditorDialogAtom,
	settingsDialogAtom,
	ttmlChecklistDialogAtom,
	submitToAMLLDBDialogAtom,
	timeShiftDialogAtom,
	timeStretchDialogAtom,
} from "$/states/dialogs.ts";
import {
	keyDeleteSelectionAtom,
	keyNewFileAtom,
	keyOpenFileAtom,
	keyRedoAtom,
	keySaveFileAtom,
	keySelectAllAtom,
	keySelectInvertedAtom,
	keySelectWordsOfMatchedSelectionAtom,
	keyUndoAtom,
} from "$/states/keybindings.ts";
import {
	isDirtyAtom,
	lyricLinesAtom,
	newLyricLinesAtom,
	projectIdAtom,
	redoLyricLinesAtom,
	saveFileNameAtom,
	selectedLinesAtom,
	selectedWordsAtom,
	undoableLyricLinesAtom,
	undoLyricLinesAtom,
} from "$/states/main.ts";
import { type LyricWord, type LyricWordBase, newLyricWord } from "$/types/ttml";
import { error, log } from "$/utils/logging.ts";

export const useTopMenuActions = () => {
	const { t } = useTranslation();
	const [saveFileName, setSaveFileName] = useAtom(saveFileNameAtom);
	const newLyricLine = useSetAtom(newLyricLinesAtom);
	const editLyricLines = useSetImmerAtom(lyricLinesAtom);
	const setMetadataEditorOpened = useSetAtom(metadataEditorDialogAtom);
	const setSettingsDialogOpened = useSetAtom(settingsDialogAtom);
	const undoLyricLines = useAtomValue(undoableLyricLinesAtom);
	const store = useStore();
	const isDirty = useAtomValue(isDirtyAtom);
	const setConfirmDialog = useSetAtom(confirmDialogAtom);
	const setHistoryRestoreDialog = useSetAtom(historyRestoreDialogAtom);
	const setAdvancedSegmentationDialog = useSetAtom(
		advancedSegmentationDialogAtom,
	);
	const setAutoSegmentDialog = useSetAtom(autoSegmentDialogAtom);
	const savedSegmentationEngine = useAtomValue(segmentationEngineAtom);
	const setSplitEnglish = useSetAtom(segmentationSplitEnglishAtom);
	const setLearnedSplitsDialog = useSetAtom(learnedSplitsDialogAtom);
	const setTimeShiftDialog = useSetAtom(timeShiftDialogAtom);
	const setTimeStretchDialog = useSetAtom(timeStretchDialogAtom);
	const setTTMLChecklistDialog = useSetAtom(ttmlChecklistDialogAtom);
	const { openFile } = useFileOpener();
	const setProjectId = useSetAtom(projectIdAtom);
	const { config: segmentationConfig } = useSegmentationConfig();
	const lyricLines = useAtomValue(lyricLinesAtom);
	const newFileKey = useAtomValue(keyNewFileAtom);
	const openFileKey = useAtomValue(keyOpenFileAtom);
	const saveFileKey = useAtomValue(keySaveFileAtom);
	const undoKey = useAtomValue(keyUndoAtom);
	const redoKey = useAtomValue(keyRedoAtom);
	const selectAllLinesKey = useAtomValue(keySelectAllAtom);
	const selectInvertedLinesKey = useAtomValue(keySelectInvertedAtom);
	const selectWordsOfMatchedSelectionKey = useAtomValue(
		keySelectWordsOfMatchedSelectionAtom,
	);
	const deleteSelectionKey = useAtomValue(keyDeleteSelectionAtom);

	const buildRubySegments = useCallback(
		(text: string, baseWord: LyricWordBase) => {
			const sourceWord: LyricWord = {
				...newLyricWord(),
				word: text,
				startTime: baseWord.startTime,
				endTime: baseWord.endTime,
				emptyBeat: 0,
			};
			const segments = segmentWord(sourceWord, segmentationConfig);
			if (segments.length === 0) {
				return [
					{
						word: text,
						startTime: baseWord.startTime,
						endTime: baseWord.endTime,
					},
				];
			}
			return segments.map((segment) => ({
				word: segment.word,
				startTime: segment.startTime,
				endTime: segment.endTime,
			}));
		},
		[segmentationConfig],
	);

	const onNewFile = useCallback(() => {
		const action = () => {
			newLyricLine();
			setProjectId(uid());
			setSaveFileName("lyric.ttml");
		};

		if (isDirty) {
			setConfirmDialog({
				open: true,
				title: t("confirmDialog.newFile.title", "确认新建文件"),
				description: t(
					"confirmDialog.newFile.description",
					"当前文件有未保存的更改。如果继续，这些更改将会丢失。确定要新建文件吗？",
				),
				onConfirm: action,
			});
		} else {
			action();
		}
	}, [
		isDirty,
		newLyricLine,
		setConfirmDialog,
		t,
		setProjectId,
		setSaveFileName,
	]);

	const onOpenFile = useCallback(() => {
		const inputEl = document.createElement("input");
		inputEl.type = "file";
		inputEl.accept = ".ttml,.lrc,.qrc,.eslrc,.lys,.yrc,*/*";
		inputEl.addEventListener(
			"change",
			() => {
				const file = inputEl.files?.[0];
				if (!file) return;
				openFile(file);
			},
			{
				once: true,
			},
		);
		inputEl.click();
	}, [openFile]);

	const onOpenFileFromClipboard = useCallback(async () => {
		try {
			const ttmlText = await navigator.clipboard.readText();
			const file = new File([ttmlText], "lyric.ttml", {
				type: "application/xml",
			});
			openFile(file);
		} catch (e) {
			error("Failed to parse TTML file from clipboard", e);
		}
	}, [openFile]);

	const onSaveFile = useCallback(async () => {
		const action = async () => {
			try {
				const currentLyrics = store.get(lyricLinesAtom);
				const sectionIssues = validateSections(currentLyrics);
				if (sectionIssues.length > 0) {
					toast.info(
						`Section review: ${sectionIssues.length} non-blocking issue${sectionIssues.length === 1 ? "" : "s"}.`,
					);
				}
				const ttmlText = exportTTMLText(currentLyrics);
				const savedName = await saveFile(ttmlText, {
					suggestedName: saveFileName,
					types: [
						{
							description: "TTML Files",
							accept: { "application/ttml+xml": [".ttml"] },
						},
					],
				});
				if (savedName) setSaveFileName(savedName);
			} catch (e) {
				error("Failed to save TTML file", e);
			}
		};

		const lyrics = store.get(lyricLinesAtom);
		const firstUntimedLine = lyrics.lyricLines.find((line) => line.endTime === 0 && getSynchronizableUnits(line).length > 0);
		let untimedWord: LyricWord | undefined;
		let untimedLine: import("$/types/ttml").LyricLine | undefined;

		if (firstUntimedLine) {
			untimedLine = firstUntimedLine;
			untimedWord = getSynchronizableUnits(firstUntimedLine)[0].word;
		} else {
			untimedLine = lyrics.lyricLines.find((line) => getSynchronizableUnits(line).some((u) => u.word.endTime === 0));
			if (untimedLine) {
				untimedWord = getSynchronizableUnits(untimedLine).find((u) => u.word.endTime === 0)?.word;
			}
		}

		if (untimedLine && untimedWord) {
			setConfirmDialog({
				open: true,
				title: t("confirmDialog.untimedLyrics.title", "Untimed Lyrics Detected"),
				description: t("confirmDialog.untimedLyrics.description", "There is an untimed line or word in your lyrics. Would you like to fix it or export anyway?"),
				confirmText: t("confirmDialog.untimedLyrics.exportAnyway", "Export Anyway"),
				cancelText: t("confirmDialog.untimedLyrics.fixIt", "Fix It"),
				onConfirm: action,
				onCancel: () => {
					store.set(selectedLinesAtom, new Set([untimedLine!.id]));
					store.set(selectedWordsAtom, new Set([untimedWord!.id]));
					audioEngine.seekMusic(untimedLine!.startTime / 1000);
					store.set(currentTimeAtom, untimedLine!.startTime);
				}
			});
		} else {
			action();
		}
	}, [saveFileName, store, setSaveFileName, setConfirmDialog, t]);

	const onOpenHistoryRestore = useCallback(() => {
		setHistoryRestoreDialog(true);
	}, [setHistoryRestoreDialog]);

	const onSaveFileToClipboard = useCallback(async () => {
		const action = async () => {
			try {
				const lyric = store.get(lyricLinesAtom);
				const sectionIssues = validateSections(lyric);
				if (sectionIssues.length > 0) {
					toast.info(
						`Section review: ${sectionIssues.length} non-blocking issue${sectionIssues.length === 1 ? "" : "s"}.`,
					);
				}
				const ttml = exportTTMLText(lyric);
				await navigator.clipboard.writeText(ttml);
			} catch (e) {
				error("Failed to save TTML file into clipboard", e);
			}
		};

		const lyrics = store.get(lyricLinesAtom);
		const firstUntimedLine = lyrics.lyricLines.find((line) => line.endTime === 0 && getSynchronizableUnits(line).length > 0);
		let untimedWord: LyricWord | undefined;
		let untimedLine: import("$/types/ttml").LyricLine | undefined;

		if (firstUntimedLine) {
			untimedLine = firstUntimedLine;
			untimedWord = getSynchronizableUnits(firstUntimedLine)[0].word;
		} else {
			untimedLine = lyrics.lyricLines.find((line) => getSynchronizableUnits(line).some((u) => u.word.endTime === 0));
			if (untimedLine) {
				untimedWord = getSynchronizableUnits(untimedLine).find((u) => u.word.endTime === 0)?.word;
			}
		}

		if (untimedLine && untimedWord) {
			setConfirmDialog({
				open: true,
				title: t("confirmDialog.untimedLyrics.title", "Untimed Lyrics Detected"),
				description: t("confirmDialog.untimedLyrics.description", "There is an untimed line or word in your lyrics. Would you like to fix it or export anyway?"),
				confirmText: t("confirmDialog.untimedLyrics.exportAnyway", "Export Anyway"),
				cancelText: t("confirmDialog.untimedLyrics.fixIt", "Fix It"),
				onConfirm: action,
				onCancel: () => {
					store.set(selectedLinesAtom, new Set([untimedLine!.id]));
					store.set(selectedWordsAtom, new Set([untimedWord!.id]));
					audioEngine.seekMusic(untimedLine!.startTime / 1000);
					store.set(currentTimeAtom, untimedLine!.startTime);
				}
			});
		} else {
			action();
		}
	}, [store, setConfirmDialog, t]);

	const onSubmitToAMLLDB = useCallback(() => {
		store.set(submitToAMLLDBDialogAtom, true);
	}, [store]);

	const onOpenMetadataEditor = useCallback(() => {
		setMetadataEditorOpened(true);
	}, [setMetadataEditorOpened]);

	const onOpenSettings = useCallback(() => {
		setSettingsDialogOpened(true);
	}, [setSettingsDialogOpened]);

	const onOpenLatencyTest = useCallback(() => {
		store.set(latencyTestDialogAtom, true);
	}, [store]);

	const onOpenTTMLChecklist = useCallback(() => {
		setTTMLChecklistDialog(true);
	}, [setTTMLChecklistDialog]);

	const onOpenGitHub = useCallback(async () => {
		if (import.meta.env.TAURI_ENV_PLATFORM) {
			await open("https://github.com/NaeNaeTart/NaeNae-AMLL-TTML-TOOL");
		} else {
			window.open("https://github.com/NaeNaeTart/NaeNae-AMLL-TTML-TOOL");
		}
	}, []);

	const onOpenWiki = useCallback(async () => {
		if (import.meta.env.TAURI_ENV_PLATFORM) {
			await open("https://github.com/NaeNaeTart/NaeNae-AMLL-TTML-TOOL/wiki");
		} else {
			window.open("https://github.com/NaeNaeTart/NaeNae-AMLL-TTML-TOOL/wiki");
		}
	}, []);

	const onUndo = useCallback(() => {
		store.set(undoLyricLinesAtom);
	}, [store]);

	const onRedo = useCallback(() => {
		store.set(redoLyricLinesAtom);
	}, [store]);

	const onUnselectAll = useCallback(() => {
		const immerSelectedLinesAtom = withImmer(selectedLinesAtom);
		const immerSelectedWordsAtom = withImmer(selectedWordsAtom);
		store.set(immerSelectedLinesAtom, (old) => {
			old.clear();
		});
		store.set(immerSelectedWordsAtom, (old) => {
			old.clear();
		});
	}, [store]);

	const onSelectAll = useCallback(() => {
		const lines = store.get(lyricLinesAtom).lyricLines;
		const selectedLineIds = store.get(selectedLinesAtom);
		const selectedLines = lines.filter((l) => selectedLineIds.has(l.id));
		const selectedWordIds = store.get(selectedWordsAtom);
		const selectedWords = lines
			.flatMap((l) => l.words)
			.filter((w) => selectedWordIds.has(w.id));
		if (selectedWords.length > 0) {
			const tmpWordIds = new Set(selectedWordIds);
			for (const selLine of selectedLines) {
				for (const word of selLine.words) {
					tmpWordIds.delete(word.id);
				}
			}
			if (tmpWordIds.size === 0) {
				store.set(
					selectedWordsAtom,
					new Set(selectedLines.flatMap((line) => line.words.map((w) => w.id))),
				);
				return;
			}
		} else {
			store.set(
				selectedLinesAtom,
				new Set(store.get(lyricLinesAtom).lyricLines.map((l) => l.id)),
			);
		}
		const sel = window.getSelection();
		if (sel) {
			if (sel.empty) {
				sel.empty();
			} else if (sel.removeAllRanges) {
				sel.removeAllRanges();
			}
		}
	}, [store]);

	const onSelectInverted = useCallback(() => {}, []);

	const onSelectWordsOfMatchedSelection = useCallback(() => {}, []);

	const onDeleteSelection = useCallback(() => {
		const selectedWordIds = store.get(selectedWordsAtom);
		const selectedLineIds = store.get(selectedLinesAtom);
		log("deleting selections", selectedWordIds, selectedLineIds);
		if (selectedWordIds.size === 0) {
			editLyricLines((prev) => {
				prev.lyricLines = prev.lyricLines.filter(
					(l) => !selectedLineIds.has(l.id),
				);
			});
		} else {
			editLyricLines((prev) => {
				for (const line of prev.lyricLines) {
					line.words = line.words.filter((w) => !selectedWordIds.has(w.id));
				}
			});
		}
		store.set(selectedWordsAtom, new Set());
		store.set(selectedLinesAtom, new Set());
	}, [store, editLyricLines]);

	const onAutoSegment = useCallback(() => {
		setAutoSegmentDialog(true);
	}, [setAutoSegmentDialog]);

	const onQuickAutoSegment = useCallback(() => {
		if (
			!matchesSavedSyllabificationEngine(
				lyricLines.lyricLines,
				savedSegmentationEngine,
			)
		) {
			setAutoSegmentDialog(true);
			return;
		}

		setSplitEnglish(savedSegmentationEngine !== "none");
		editLyricLines((draft) => {
			draft.lyricLines = segmentLyricLines(draft.lyricLines, {
				...segmentationConfig,
				engine: savedSegmentationEngine,
				splitEnglish: savedSegmentationEngine !== "none",
			});
		});
		toast.success(
			t("autoSegmentApplied", {
				defaultValue: "Auto-segmented with {engine}",
				engine:
					SYLLABIFICATION_ENGINES.find(
						({ id }) => id === savedSegmentationEngine,
					)?.name ?? savedSegmentationEngine,
			}),
		);
	}, [
		editLyricLines,
		lyricLines.lyricLines,
		savedSegmentationEngine,
		segmentationConfig,
		setAutoSegmentDialog,
		setSplitEnglish,
		t,
	]);

	const onRubySegment = useCallback(() => {
		const selectedWordIds = store.get(selectedWordsAtom);
		const hasSelection = selectedWordIds.size > 0;
		editLyricLines((state) => {
			for (const line of state.lyricLines) {
				for (const word of line.words) {
					if (hasSelection && !selectedWordIds.has(word.id)) continue;
					if (!word.ruby || word.ruby.length === 0) continue;
					const nextRuby: LyricWordBase[] = [];
					for (const rubyWord of word.ruby) {
						const parts = rubyWord.word.split("|");
						const nextSegments = buildRubySegments(parts[0] ?? "", rubyWord);
						const fallbackBase = {
							word: "",
							startTime: word.startTime,
							endTime: word.endTime,
						};
						const extraSegments = parts
							.slice(1)
							.flatMap((part) => buildRubySegments(part, fallbackBase));
						nextRuby.push(...nextSegments, ...extraSegments);
					}
					word.ruby = nextRuby;
				}
			}
		});
	}, [buildRubySegments, editLyricLines, store]);

	const onOpenTimeShift = useCallback(() => {
		setTimeShiftDialog(true);
	}, [setTimeShiftDialog]);

	const onOpenTimeStretch = useCallback(() => {
		setTimeStretchDialog(true);
	}, [setTimeStretchDialog]);

	const onSyncLineTimestamps = useCallback(() => {
		const action = () => {
			editLyricLines((draft) => {
				for (let i = 0; i < draft.lyricLines.length; i++) {
					const line = draft.lyricLines[i];
					if (line.words.length === 0) continue;

					let startTime = line.words[0].startTime;
					let endTime = line.words[line.words.length - 1].endTime;

					if (i + 1 < draft.lyricLines.length) {
						const nextLine = draft.lyricLines[i + 1];
						if (nextLine.isBG && nextLine.words.length > 0) {
							const nextLineStart = nextLine.words[0].startTime;
							const nextLineEnd =
								nextLine.words[nextLine.words.length - 1].endTime;
							startTime = Math.min(startTime, nextLineStart);
							endTime = Math.max(endTime, nextLineEnd);
						}
					}

					line.startTime = startTime;
					line.endTime = endTime;
				}
			});
		};

		setConfirmDialog({
			open: true,
			title: t("confirmDialog.syncLineTimestamps.title", "确认同步行时间戳"),
			description: t(
				"confirmDialog.syncLineTimestamps.description",
				"此操作将根据每行单词的时间戳自动同步所有行的起始和结束时间为第一个和最后一个音节的开始和结束时间。确定要继续吗？",
			),
			onConfirm: action,
		});
	}, [editLyricLines, setConfirmDialog, t]);



	const onOpenAdvancedSegmentation = useCallback(() => {
		setAdvancedSegmentationDialog(true);
	}, [setAdvancedSegmentationDialog]);
	const onOpenLearnedSplits = useCallback(() => {
		setLearnedSplitsDialog(true);
	}, [setLearnedSplitsDialog]);


	return {
		newFileKey,
		openFileKey,
		saveFileKey,
		undoKey,
		redoKey,
		selectAllLinesKey,
		unselectAllLinesKey: selectAllLinesKey,
		selectInvertedLinesKey,
		selectWordsOfMatchedSelectionKey,
		deleteSelectionKey,
		undoDisabled: !undoLyricLines.canUndo,
		redoDisabled: !undoLyricLines.canRedo,
		onNewFile,
		onOpenFile,
		onOpenFileFromClipboard,
		onSaveFile,
		onOpenHistoryRestore,
		onSaveFileToClipboard,
		onSubmitToAMLLDB,
		onUndo,
		onRedo,
		onSelectAll,
		onUnselectAll,
		onSelectInverted,
		onSelectWordsOfMatchedSelection,
		onDeleteSelection,
		onOpenTimeShift,
		onOpenTimeStretch,
		onOpenMetadataEditor,
		onOpenSettings,
		onAutoSegment,
		onQuickAutoSegment,
		onRubySegment,
		onOpenAdvancedSegmentation,
		onOpenLearnedSplits,
		onSyncLineTimestamps,
		onOpenLatencyTest,
		onOpenTTMLChecklist,
		onOpenGitHub,
		onOpenWiki,
	};
};
